import { AxiosResponse } from 'axios'
import { API_ROUTES } from 'constants/apiRoutesConstants'
import {
	AccountSettings,
	CompanyInfoUpdatePayload,
	CountryOption,
	GeneralInfoUpdatePayload,
	Language,
} from 'features/account/accountTypes'
import { User } from 'types/auth'
import { UserMarketingFlowStatusEnum } from 'types/subscription'

import { getApiClient } from './apiClient'

class UserSettingsApi {
	public async reviewBannerClicked(): Promise<void> {
		await getApiClient().post(API_ROUTES.userSettings.bannerClicked)
	}

	public async setFirstLoginMarketing(
		marketingFlowState: UserMarketingFlowStatusEnum,
	): Promise<void> {
		await getApiClient().put(API_ROUTES.userSettings.firstLoginMarketing, {
			status: marketingFlowState,
		})
	}

	public async updateGeneralInfo(
		payload: GeneralInfoUpdatePayload,
	): Promise<void> {
		await getApiClient().put(API_ROUTES.userSettings.generalInfo, payload)
	}

	public async updateCompanyInfo(
		payload: CompanyInfoUpdatePayload,
	): Promise<void> {
		await getApiClient().put(API_ROUTES.userSettings.companyInfo, payload)
	}

	public async getCountries(
		language: string,
	): Promise<AxiosResponse<CountryOption[]>> {
		return await getApiClient().get(API_ROUTES.userSettings.getCountries, {
			params: { language },
		})
	}

	public async userSettings(): Promise<AxiosResponse<AccountSettings>> {
		return await getApiClient().get(API_ROUTES.userSettings.getSettingsInfo)
	}

	public async getUser(): Promise<AxiosResponse<User>> {
		return await getApiClient().get(API_ROUTES.userSettings.getUser)
	}

	public async changePassword(password: string): Promise<AxiosResponse<void>> {
		return await getApiClient().put(API_ROUTES.userSettings.changePassword, {
			password,
		})
	}

	public async disable2FA(code: string) {
		return await getApiClient().put(API_ROUTES.auth.disable2fa, { code })
	}

	public async getNativeLanguages(): Promise<Language[]> {
		const { data } = await getApiClient().get(
			API_ROUTES.userSettings.getNativeLanguages,
		)
		return data
	}

	public async changeLanguage(
		languageCode: string,
	): Promise<AxiosResponse<void>> {
		return await getApiClient().put(API_ROUTES.userSettings.changeLanguage, {
			languageCode,
		})
	}

	public async changeEmail(email: string): Promise<AxiosResponse<void>> {
		return await getApiClient().put(API_ROUTES.userSettings.changeEmail, {
			email,
		})
	}

	public async changeTimeZone(timezone: string): Promise<AxiosResponse<void>> {
		return await getApiClient().put(API_ROUTES.userSettings.changeTimezone, {
			timezone,
		})
	}

	public async getTimeZones(
		language: string,
	): Promise<{ name: string; displayName: string }[]> {
		const { data } = await getApiClient().get(
			API_ROUTES.userSettings.getTimeZone,
			{
				params: { language },
			},
		)
		return data
	}

	public async enable2FA(user: User) {
		return await getApiClient().put<{
			img: string
			userId: string
			secret: string
		}>(API_ROUTES.userSettings.enable2Fa, { user })
	}
}

export const userSettingsApi = new UserSettingsApi()
